<script lang="ts" setup>
const props = withDefaults(defineProps<{ data: ReviewGBF, cardClass?: string, hasParent?: boolean, isOpen?: boolean, level?: number }>(), {
  level: 1,
})
const { userId, user } = useUser()
const { openModal } = useGlobalModal()
const shareVisible = ref(false)
function openShare() {
  shareVisible.value = true
}
const router = useRouter()
function unHighlight() {
  router.replace({ query: { ...route.query, review: undefined } })
}

const route = useRoute()
const isInRouteParams = computed(() => route?.query?.review && route.query.review === props.data.id)

watch(isInRouteParams, (val) => {
  if (val && !props.hasParent) {
    openModal({
      title: 'Review',
      description: props.data.review,
      confirm: () => {},
      cancel: () => unHighlight(),
      component: 'reviewGBFCard',
      componentProps: {
        data: props.data,
        hasParent: props.hasParent,
        isOpen: true,
        level: props.level + 1,
      },
    })
  }
}, { immediate: true })

useSeoMeta({
  title: isInRouteParams.value ? 'Review' : undefined,
  ogTitle: isInRouteParams.value ? 'Review' : undefined,
  description: isInRouteParams.value ? props.data.review : undefined,
  ogDescription: isInRouteParams.value ? props.data.review : undefined,
  // ogImage: 'https://example.com/image.png',
  // twitterCard: 'summary_large_image',
})
</script>

<template>
  <div class="flex flex-col gap-3">
    <UICard
      class="overflow-clip relative text-blue font-semibold"
      :class="cardClass"
    >
      <UModal
        v-model="shareVisible"
        :ui="{ background: 'bg-pink' }"
      >
        <div class="p-6 md:p-8 lg:p-12 flex flex-col gap-3">
          <HeadingText
            tag="h4"
            class="text-white"
          >
            Share this review with your network
          </HeadingText>
          <SocialShare
            facebook
            twitter
            linked-in
            direct-link
            :added-url="`?review=${data.id}&type=gbf`"
          />
        </div>
      </UModal>
      <UIcon
        v-if="!hasParent"
        name="ri:double-quotes-l"
        class="w-16 h-16 text-yellow absolute top-6 left-6"
      />
      <div
        class=" rounded-full overflow-clip flex items-center justify-center shadow-lg mx-auto"
        :class="[hasParent ? 'w-8 h-8' :'w-12 h-12 md:h-20 md:w-20 lg:w-28 lg:h-28  ']"
      >
        <ImageFull
          v-if="data.icon"
          :id="data.id"
          :src="data.icon"
          :alt="data.reviewer"
          sizes="(max-width: 767px) 48px, (max-width: 1023px) 80px, 112px"
          loading="lazy"
          :collection-id="data.collectionId"
          class="h-full w-full"
        />
        <ImageFull
          v-else-if="data.expand?.business?.name"
          :id="data.id"
          :src="data.expand?.business?.logo"
          :alt="data.reviewer"
          sizes="(max-width: 767px) 48px, (max-width: 1023px) 80px, 112px"
          loading="lazy"
          :collection-id="data.collectionId"
          class="h-full w-full"
        />

        <UIcon
          v-else
          name="heroicons:user-20-solid"
          class=" text-white"
          :class="[hasParent ? 'w-6 h-6' : 'w-10 h-10 md:h-16 md:w-16 lg:w-20 lg:h-20']"
        />
      </div>
      <div class="flex justify-center gap-6 items-center mx-auto pt-6">
        <div class="flex flex-col">
          <p class="font-bold md:text-lg lg:text-xl">
            {{ data.reviewerName }}
          </p>
          <NuxtLink
            v-if="data.expand?.business?.name"
            :to="getPublicRoute('businesses', { slug: data.expand?.business?.slug })"
            class="text-pink"
          >
            {{ data.businessName }}
          </NuxtLink>
        </div>
      </div>
      <div class="relative flex flex-col h-full pt-2 justify-center">
        <CollectionBusinessRating
          v-if="data.stars"
          :rating="data.stars"
          class="text-yellow ml-auto"
        />
        <p class="md:text-lg lg:text-xl pt-3 flex flex-1 items-center">
          {{ data.review }}
        </p>

        <div class="flex flex-col gap-6 items-start pt-6 ">
          <div class="gap-3 flex flex-wrap items-center">
            <UTooltip
              v-if="!hasParent"
              text="Share"
              @click="openShare"
            >
              <UButton
                variant="soft"
                aria-label="Share Review"
                rounded
                icon="ri:share-line"
              />
            </UTooltip>
          </div>
        </div>
      </div>
    </UICard>
  </div>
</template>
